import { useCallback, useEffect, useRef, useState } from "react";
import { beautyAgendaApi } from "../../../api/organizerBeautyApi";
import { mensajeDe } from "./agendaLayout";

/* Carga la agenda de un día (GET /agenda). Una respuesta vieja nunca pisa a una más nueva. */
export default function useBeautyAgenda({ date, includeCancelled = false, enabled = true }) {
  const [agenda, setAgenda] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const requestIdRef = useRef(0);

  const load = useCallback(
    async ({ propagateError = false } = {}) => {
      if (!enabled || !date) return null;
      const requestId = ++requestIdRef.current;
      setState("loading");
      setMessage("");
      try {
        const res = await beautyAgendaApi(date, { includeCancelled: Boolean(includeCancelled) });
        if (requestId !== requestIdRef.current) return null;
        const info = res?.data?.info;
        if (!info || !Array.isArray(info.professionals)) {
          throw new Error("La respuesta de la agenda no tiene un formato válido.");
        }
        setAgenda(info);
        setState("success");
        return info;
      } catch (error) {
        if (requestId !== requestIdRef.current) return null;
        setMessage(mensajeDe(error, "No pudimos cargar la agenda. Probá de nuevo."));
        setState("error");
        if (propagateError) throw error;
        return null;
      }
    },
    [date, includeCancelled, enabled]
  );

  // Al cambiar de día o del filtro se arranca de cero; un `reload` conserva lo que se ve.
  useEffect(() => {
    if (!enabled || !date) {
      requestIdRef.current += 1;
      setAgenda(null);
      setState("idle");
      return undefined;
    }
    setAgenda(null);
    load().catch(() => {});
    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, date, includeCancelled, load]);

  const reload = useCallback(() => {
    load().catch(() => {});
  }, [load]);
  const reloadAndWait = useCallback(() => load({ propagateError: true }), [load]);

  return { agenda, state, message, reload, reloadAndWait };
}
